import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Sparkles, Users, Globe } from "lucide-react";

const FinalCTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const highlights = [
    {
      icon: Sparkles,
      title: "Des outils Puissants",
      description: "Novateurs et à la pointe du principe de la résonance pour accompagner vos clients sans mental, sans décodage, ni interprétations.",
    },
    {
      icon: Users,
      title: "Une méthode clé en main",
      description: "D'accompagnement prête à l'emploi et à être déployée quelque soit votre niveau d'expérience et d'expertise.",
    },
    {
      icon: Globe,
      title: "Des protocoles précis",
      description: "Pour structurer vos séances en phase avec vos valeurs, donnant du sens à votre travail sans entretenir de dépendance.",
    },
  ];

  return (
    <section
      ref={ref}
      data-testid="final-cta-section"
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "#030014" }}
    >
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1516331138075-f3adc1e149cd?auto=format&fit=crop&w=2000&q=80')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/80 to-[#030014]" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-8 leading-relaxed"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Nous avons le devoir de construire le{" "}
            <span className="text-gradient-cosmic">monde de demain</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Notre métier de thérapeute sera un enjeu de société pour accompagner de façon 
            <strong className="text-white"> Juste et Impartiale</strong> le maximum de personnes.
          </p>
        </motion.div>

        {/* Highlights Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {highlights.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
                className="glass-card p-6 rounded-xl text-center"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-8 h-8 text-cyan-400" />
                </div>
                <h3
                  className="text-xl font-light text-white mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {item.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center"
        >
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Alors ne ratez pas le train en marche et soyez{" "}
            <span className="text-amber-400 font-medium">pionniers</span> de la nouvelle 
            approche énergétique au service du déploiement de la Conscience collective.
          </p>

          <a
            href="https://gillesdelieuze.com/checkout-seminaire-abondance-2/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-button"
            className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-4"
          >
            Je Participe à la Révolution Quantique
            <ArrowRight className="w-5 h-5" />
          </a>

          {/* Extra Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-slate-500"
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cyan-400" />
              24 modules en ligne
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-purple-400" />
              Séances présentiel ou distance
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              Communauté d'Artistes Évolutionnaires
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTASection;
